// src/pages/RsoTermsPage.jsx
import React from 'react';
import useLegalLanguage from '../hooks/useLegalLanguage';
import styled from 'styled-components';
import { PageTitle, LanguageToggle, LangButton, ContactBlock, InfoBlock } from '../components/ui/PageElements';
import PageMeta from '../components/PageMeta';

const LegalWrapper = styled.div`
    max-width: 800px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    line-height: 1.7;

    h2 {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-top: 40px;
        margin-bottom: 20px;
        color: var(--primary-color);
        position: relative;
        &:before {
            content: "§";
            color: var(--secondary-color);
            margin-right: 10px;
            opacity: 0.7;
        }
    }

    h3 {
        font-size: 18px;
        font-weight: 700;
        margin-top: 30px;
        margin-bottom: 15px;
        color: var(--text-color);
        opacity: 0.9;
    }

    p, li {
        font-size: 16px;
        color: #cccccc;
        margin-bottom: 15px;
    }

    ul {
        list-style: disc;
        padding-left: 30px;
        margin-bottom: 20px;
    }

    a {
        color: var(--secondary-color);
        text-decoration: underline;
        text-underline-offset: 3px;
        &:hover {
            color: var(--primary-color);
        }
    }

    strong {
        color: var(--text-color);
        font-weight: 700;
    }
`;

const LastUpdatedText = styled.p`
    font-style: italic;
    color: var(--text-muted);
    margin-bottom: 30px !important;
    font-size: 14px !important;
`;

const ImportantBox = styled.div`
    background: rgba(255, 51, 153, 0.1);
    border: 1px solid var(--primary-color);
    padding: 20px;
    margin: 20px 0;

    p {
        margin-bottom: 10px;
        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const CURRENT_DATE = new Date().toLocaleDateString('en-CA');

const content = {
    en: {
        title: "RSO Terms of Service & EULA",
        lastUpdated: "Last Updated",
        intro: `These Terms of Service ("Terms") govern your purchase and use of the RSO software synthesizer ("Software")
            provided by Lucas Perron ("LPERRON_DEV", "I", "me"). By purchasing, downloading, or using the Software,
            you agree to be bound by these Terms.`,

        // Section 1: License
        section1Title: "1. Software License (EULA)",
        section1GrantTitle: "Grant of License:",
        section1GrantText: "Upon your purchase of the Software, I grant you a limited, non-exclusive, non-transferable, revocable license to install and use the Software on a reasonable number of your own devices for your personal or professional creative work.",
        section1RestrictionsTitle: "Restrictions:",
        section1RestrictionsIntro: "You may NOT:",
        section1Restrictions: [
            "Redistribute, sell, rent, lease, sublicense, or otherwise transfer the Software to any third party.",
            "Reverse engineer, decompile, or disassemble the Software, except to the extent that such activity is expressly permitted by applicable law.",
            "Modify or create derivative works based on the Software.",
            "Remove any copyright or proprietary notices from the Software."
        ],

        // Section 2: Payments
        section2Title: "2. Payment & Transaction Processing",
        section2Text: "Payments for the Software are processed through either Paddle (Merchant of Record) when purchased via lperron.com, or Gumroad when purchased via their platform. Both payment processors handle all payment details, transactions, and applicable tax collection. Your purchase is subject to their respective terms and privacy policies. License keys are generated by us for Paddle purchases and by Gumroad for their purchases.",

        // Section 3: Refund Policy
        section3Title: "3. Refunds & Right of Withdrawal",
        section3ImportantTitle: "14-Day Refund Period:",
        section3ImportantText: "You may request a full refund within 14 days of your purchase, no questions asked. While not required, we'd appreciate hearing about your experience if you're willing to share—it helps us improve RSO for everyone. Paddle purchases: Refunds are processed by Paddle in accordance with their Buyer Terms. Gumroad purchases: Refunds are processed by Gumroad in accordance with their refund policy.",
        section3LegalBasis: "",
        section3Consequence: "Upon refund (regardless of platform), your license key will be revoked and the Software will revert to Demo Mode. We encourage you to try the free demo version before purchasing—it offers full functionality with no time limit, only preset saving and DAW state recall are disabled.",

        // Section 4: Legal Guarantees (NEW)
        section4Title: "4. Legal Guarantees",
        section4Intro: "As a consumer based in the European Union, you benefit from the following legal guarantees:",
        section4ConformityTitle: "Legal Guarantee of Conformity:",
        section4ConformityText: "For digital content, you benefit from a 2-year legal guarantee of conformity from the date of delivery. If the Software does not conform to its description or does not function as reasonably expected, you may request that it be brought into conformity, or, if that is not possible, obtain a price reduction or full refund.",
        section4ConformityLegal: "Legal basis: Articles L224-25-1 to L224-25-32 of the French Consumer Code.",
        section4DefectsTitle: "Guarantee Against Hidden Defects:",
        section4DefectsText: "You may also invoke the guarantee against hidden defects under Articles 1641 to 1649 of the French Civil Code. This allows you to request a refund if the Software has a hidden defect that makes it unfit for its intended use.",

        // Section 5: Intellectual Property
        section5Title: "5. Intellectual Property",
        section5Text: "The Software, including its code, design, user interface, and branding, is the exclusive intellectual property of Lucas Perron and is protected by copyright and other intellectual property laws. Your license does not grant you any ownership rights in the Software.",

        // Section 6: Disclaimer
        section6Title: "6. Disclaimer of Warranties",
        section6Text: "THE SOFTWARE IS PROVIDED \"AS IS,\" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT. This disclaimer does not affect your statutory rights under the legal guarantees described in Section 4.",

        // Section 7: Liability
        section7Title: "7. Limitation of Liability",
        section7Text: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, I SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SOFTWARE. This limitation does not apply to damages caused by gross negligence or willful misconduct.",

        // Section 8: Mediation (NEW)
        section8Title: "8. Dispute Resolution",
        section8Intro: "In case of a dispute between you (the Client) and the company, we will endeavor to resolve it amicably (the Client shall send a written complaint to the professional).",
        section8Steps: [
            "If no amicable agreement is reached or in the absence of a response from the professional within a reasonable period of one (1) month, the consumer Client within the meaning of Article L.612-2 of the Consumer Code may refer the matter free of charge to the competent consumer mediator.",
            "The designated mediator is: Société Médiation Professionnelle"
        ],
        section8MediatorIntro: "Mediator Contact Details:",
        section8MediatorName: "La Société Médiation Professionnelle",
        section8MediatorAddress: "Alteritae, 5 rue Salvaing",
        section8MediatorCity: "12000 Rodez",
        section8MediatorWebsite: "www.mediateur-consommation-smp.fr",
        section8MediatorLink: "http://www.mediateur-consommation-smp.fr",
        section8Court: "You also retain the right to bring legal action before the competent courts.",

        // Section 9: Governing Law
        section9Title: "9. Governing Law",
        section9Text: "These Terms are governed by French law. Any dispute arising from these Terms or your use of the Software shall be subject to the exclusive jurisdiction of the courts of Lyon, France, unless mandatory consumer protection laws provide otherwise.",

        // Section 10: Changes
        section10Title: "10. Changes to These Terms",
        section10Text: "I reserve the right to modify these Terms at any time. I will notify you of any changes by posting the new Terms on this site and updating the \"Last Updated\" date. Your continued use of the Software after any such changes constitutes your acceptance of the new Terms.",

        // Section 11: Contact
        section11Title: "11. Contact Information",
        section11Intro: "If you have any questions about these Terms, please contact me:"
    },

    fr: {
        title: "RSO Conditions Générales de Vente & EULA",
        lastUpdated: "Dernière mise à jour",
        intro: `Les présentes Conditions Générales de Vente ("CGV") régissent votre achat et votre utilisation du logiciel synthétiseur RSO ("Logiciel")
            fourni par Lucas Perron ("LPERRON_DEV", "je", "moi"). En achetant, téléchargeant ou utilisant le Logiciel,
            vous acceptez d'être lié par ces CGV.`,

        // Section 1: License
        section1Title: "1. Licence du Logiciel (EULA)",
        section1GrantTitle: "Octroi de licence :",
        section1GrantText: "Lors de votre achat du Logiciel, je vous accorde une licence limitée, non exclusive, non transférable et révocable pour installer et utiliser le Logiciel sur un nombre raisonnable de vos propres appareils pour votre travail créatif personnel ou professionnel.",
        section1RestrictionsTitle: "Restrictions :",
        section1RestrictionsIntro: "Vous ne pouvez PAS :",
        section1Restrictions: [
            "Redistribuer, vendre, louer, sous-licencier ou transférer le Logiciel à un tiers.",
            "Désassembler, décompiler ou rétro-concevoir le Logiciel, sauf dans la mesure où cela est expressément autorisé par la loi applicable.",
            "Modifier ou créer des œuvres dérivées basées sur le Logiciel.",
            "Supprimer les mentions de droits d'auteur ou de propriété du Logiciel."
        ],

        // Section 2: Payments
        section2Title: "2. Traitement des Paiements et Transactions",
        section2Text: "Les paiements pour le Logiciel sont traités soit par Paddle (Merchant of Record) lors d'achat via lperron.com, soit par Gumroad lors d'achat via leur plateforme. Les deux processeurs de paiement gèrent tous les détails de paiement, transactions et collecte des taxes applicables. Votre achat est soumis à leurs conditions respectives et politiques de confidentialité. Les clés de licence sont générées par nous pour les achats Paddle et par Gumroad pour leurs achats.",

        // Section 3: Refund Policy
        section3Title: "3. Remboursements et Droit de Rétractation",
        section3ImportantTitle: "Délai de remboursement de 14 jours :",
        section3ImportantText: "Vous pouvez demander un remboursement intégral dans les 14 jours suivant votre achat, sans justification. Bien que ce ne soit pas obligatoire, nous apprécierions connaître votre expérience si vous souhaitez la partager—cela nous aide à améliorer RSO pour tous. Achats Paddle : Les remboursements sont traités par Paddle conformément à leurs Conditions d'Achat. Achats Gumroad : Les remboursements sont traités par Gumroad conformément à leur politique de remboursement.",
        section3LegalBasis: "",
        section3Consequence: "En cas de remboursement (quelle que soit la plateforme), votre clé de licence sera révoquée et le Logiciel reviendra en Mode Démo. Nous vous encourageons à essayer la version démo gratuite avant d'acheter—elle offre toutes les fonctionnalités sans limite de temps, seules la sauvegarde de presets et la restauration d'état du DAW sont désactivées.",

        // Section 4: Legal Guarantees (NEW)
        section4Title: "4. Garanties Légales",
        section4Intro: "En tant que consommateur basé dans l'Union Européenne, vous bénéficiez des garanties légales suivantes :",
        section4ConformityTitle: "Garantie légale de conformité :",
        section4ConformityText: "Pour les contenus numériques, vous bénéficiez d'une garantie légale de conformité de 2 ans à compter de la date de livraison. Si le Logiciel n'est pas conforme à sa description ou ne fonctionne pas comme raisonnablement attendu, vous pouvez demander sa mise en conformité ou, si cela n'est pas possible, obtenir une réduction de prix ou un remboursement intégral.",
        section4ConformityLegal: "Base légale : Articles L224-25-1 à L224-25-32 du Code de la consommation.",
        section4DefectsTitle: "Garantie des vices cachés :",
        section4DefectsText: "Vous pouvez également invoquer la garantie des vices cachés en vertu des Articles 1641 à 1649 du Code civil. Cela vous permet de demander un remboursement si le Logiciel présente un défaut caché qui le rend impropre à son usage prévu.",

        // Section 5: Intellectual Property
        section5Title: "5. Propriété Intellectuelle",
        section5Text: "Le Logiciel, y compris son code, design, interface utilisateur et image de marque, est la propriété intellectuelle exclusive de Lucas Perron et est protégé par le droit d'auteur et autres lois sur la propriété intellectuelle. Votre licence ne vous confère aucun droit de propriété sur le Logiciel.",

        // Section 6: Disclaimer
        section6Title: "6. Exclusion de Garanties",
        section6Text: "LE LOGICIEL EST FOURNI \"EN L'ÉTAT\", SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE, Y COMPRIS MAIS SANS S'Y LIMITER LES GARANTIES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER ET D'ABSENCE DE CONTREFAÇON. Cette exclusion n'affecte pas vos droits légaux au titre des garanties légales décrites à la Section 4.",

        // Section 7: Liability
        section7Title: "7. Limitation de Responsabilité",
        section7Text: "DANS LES LIMITES AUTORISÉES PAR LA LOI, JE NE SERAI PAS RESPONSABLE DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS, NI DE TOUTE PERTE DE PROFITS OU DE REVENUS, DIRECTS OU INDIRECTS, NI DE TOUTE PERTE DE DONNÉES, D'UTILISATION, DE CLIENTÈLE OU D'AUTRES PERTES INTANGIBLES RÉSULTANT DE VOTRE ACCÈS OU UTILISATION OU IMPOSSIBILITÉ D'ACCÉDER OU D'UTILISER LE LOGICIEL. Cette limitation ne s'applique pas aux dommages causés par une négligence grave ou une faute intentionnelle.",

        // Section 8: Mediation (NEW)
        section8Title: "8. Résolution des Litiges",
        section8Intro: "En cas de litige entre le Client et l’entreprise, ceux-ci s’efforceront de le résoudre à l’amiable (le Client adressera une réclamation écrite auprès du professionnel).",
        section8Steps: [
            "A défaut d’accord amiable ou en l’absence de réponse du professionnel dans un délai raisonnable d’un (1) mois, le Client consommateur au sens de l’article L.612-2 du code de la consommation a la possibilité de saisir gratuitement, si un désaccord subsiste, le médiateur compétent inscrit sur la liste des médiateurs établie par la Commission d’évaluation et de contrôle de la médiation de la consommation en application de l’article L.615-1 du code de la consommation.",
            "Le médiateur compétent est : La Société Médiation Professionnelle"
        ],
        section8MediatorIntro: "Coordonnées du Médiateur :",
        section8MediatorName: "La Société Médiation Professionnelle",
        section8MediatorAddress: "Alteritae, 5 rue Salvaing",
        section8MediatorCity: "12000 Rodez",
        section8MediatorWebsite: "www.mediateur-consommation-smp.fr",
        section8MediatorLink: "http://www.mediateur-consommation-smp.fr",
        section8Court: "Vous conservez également le droit d'engager une action en justice devant les tribunaux compétents.",

        // Section 9: Governing Law
        section9Title: "9. Droit Applicable",
        section9Text: "Les présentes CGV sont régies par le droit français. Tout litige découlant de ces CGV ou de votre utilisation du Logiciel sera soumis à la compétence exclusive des tribunaux de Lyon, France, sauf si les lois impératives de protection des consommateurs en disposent autrement.",

        // Section 10: Changes
        section10Title: "10. Modifications des CGV",
        section10Text: "Je me réserve le droit de modifier ces CGV à tout moment. Je vous informerai de tout changement en publiant les nouvelles CGV sur ce site et en mettant à jour la date \"Dernière mise à jour\". Votre utilisation continue du Logiciel après de telles modifications constitue votre acceptation des nouvelles CGV.",

        // Section 11: Contact
        section11Title: "11. Coordonnées",
        section11Intro: "Si vous avez des questions concernant ces CGV, veuillez me contacter :"
    }
};

const RsoTermsPage = () => {
    const [lang, setLang] = useLegalLanguage();
    const t = content[lang];

    return (
        <LegalWrapper>
            <PageMeta
                title="RSO Terms of Service"
                description="RSO Synthesizer Terms of Service. License agreement and terms governing the purchase and use of the RSO VST3 plugin."
                canonicalPath="/rso-terms-of-service"
            />
            <LanguageToggle>
                <LangButton $active={lang === 'en'} onClick={() => setLang('en')}>EN</LangButton>
                <LangButton $active={lang === 'fr'} onClick={() => setLang('fr')}>FR</LangButton>
            </LanguageToggle>
            <PageTitle style={{ textAlign: 'left', fontSize: '36px', marginBottom: '10px' }}>{t.title}</PageTitle>
            <LastUpdatedText>{t.lastUpdated}: {CURRENT_DATE}</LastUpdatedText>

            <p>{t.intro}</p>

            {/* Section 1: License */}
            <h2>{t.section1Title}</h2>
            <p><strong>{t.section1GrantTitle}</strong> {t.section1GrantText}</p>
            <p><strong>{t.section1RestrictionsTitle}</strong> {t.section1RestrictionsIntro}</p>
            <ul>
                {t.section1Restrictions.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            {/* Section 2: Payments */}
            <h2>{t.section2Title}</h2>
            <p>{t.section2Text}</p>

            {/* Section 3: Refund Policy */}
            <h2>{t.section3Title}</h2>
            <ImportantBox>
                <p><strong>{t.section3ImportantTitle}</strong> {t.section3ImportantText}</p>
                {t.section3LegalBasis && <p><em>{t.section3LegalBasis}</em></p>}
            </ImportantBox>
            <p>{t.section3Consequence}</p>

            {/* Section 4: Legal Guarantees */}
            <h2>{t.section4Title}</h2>
            <p>{t.section4Intro}</p>
            <h3>{t.section4ConformityTitle}</h3>
            <p>{t.section4ConformityText}</p>
            <p><em>{t.section4ConformityLegal}</em></p>
            <h3>{t.section4DefectsTitle}</h3>
            <p>{t.section4DefectsText}</p>

            {/* Section 5: Intellectual Property */}
            <h2>{t.section5Title}</h2>
            <p>{t.section5Text}</p>

            {/* Section 6: Disclaimer */}
            <h2>{t.section6Title}</h2>
            <p>{t.section6Text}</p>

            {/* Section 7: Liability */}
            <h2>{t.section7Title}</h2>
            <p>{t.section7Text}</p>

            {/* Section 8: Mediation */}
            <h2>{t.section8Title}</h2>
            <p>{t.section8Intro}</p>
            <ul>
                {t.section8Steps.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <InfoBlock>
                <p><strong>{t.section8MediatorIntro}</strong></p>
                <p>{t.section8MediatorName}</p>
                <p>{t.section8MediatorAddress}</p>
                <p>{t.section8MediatorCity}</p>
                <p><a href={t.section8MediatorLink} target="_blank" rel="noopener noreferrer">{t.section8MediatorWebsite}</a></p>
            </InfoBlock>
            <p>{t.section8Court}</p>

            {/* Section 9: Governing Law */}
            <h2>{t.section9Title}</h2>
            <p>{t.section9Text}</p>

            {/* Section 10: Changes */}
            <h2>{t.section10Title}</h2>
            <p>{t.section10Text}</p>

            {/* Section 11: Contact */}
            <h2>{t.section11Title}</h2>
            <p>{t.section11Intro}</p>
            <ContactBlock lang={lang} />
        </LegalWrapper>
    );
};

export default RsoTermsPage;
